import * as React from "react";
import { ButtonType, CustomButton, CustomSubmitButton, Form, HiddenInput, SpinnerWrapper, Table } from "soprasteria-ui-library/dist";
import { useAuth } from "../../../App";
import { ClinicModel, MunicipalityModel, UserApi } from '../../../api/UserApi';
import { formatOrganisationNumber } from "../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper";
import { MENUETTPORTAL, MENUETTPORTALKOMMUN } from "../../../utils/ApplicationConstants";
import { NavigateBackFooter } from "../../NavigateBackFooter/NavigateBackFooter";
import { ConfirmClinicSelectionModal } from "./ConfirmClinicSelectionModal";
import { ConfirmMunicipalitySelectionModal } from "./ConfirmMunicipalitySelectionModal";
import "./SelectClinic.scss";

export const SelectClinic: React.FunctionComponent<{}> = () => {
    const auth = useAuth();
    const selectedRoleGroup = auth.userRolesGroup;
    const [clinics, setClinics] = React.useState<ClinicModel[]>([]);
    const [municipalities, setMunicipalities] = React.useState<MunicipalityModel[]>([]);
    const [isApiFetching, setIsApiFetching] = React.useState<boolean>(false);
    const api = new UserApi(auth);
    const [selectedClinic, setSelectedClinic] = React.useState<ClinicModel>({ id: "0", organisationNumber: "", organisationName: "", clinicNumber: "", clinicName: " s", roleGroupId: 0, roles: [], isDefault: false });
    const [selectedMunicipality, setSelectedMunicipality] = React.useState<MunicipalityModel>({ id: "0", municipalityName: "", districtName: "", residenceGroupName: "", roleGroupId: 0, roles: [], isDefault: false });
    const adminRoleId = 2147483647;

    React.useEffect(() => {
        getClinics();
        getMunicipalities();
    }, []);

    const roles = (roles: string[], rolesGroupId: number) => {
        return (
            <>
                {
                    roles && roles.length > 0 ?
                        <ul className="role-list">
                            {roles.map((role, index) =>
                                <li key={`${rolesGroupId}-${index}`}>{role}</li>
                            )}
                        </ul>
                        : null
                }
            </>
        )
    }

    const getClinics = () => {
        setIsApiFetching(true);
        api.getClinics(MENUETTPORTAL, (clinics: ClinicModel[]) => {
            setClinics(clinics);
        }, () => {
        }, () => {
            setIsApiFetching(false);
        });
    }

    const getMunicipalities = () => {
        setIsApiFetching(true);
        api.getMunicipalities(MENUETTPORTALKOMMUN, (municipalities: MunicipalityModel[]) => {
            setMunicipalities(municipalities);
        }, () => {
        }, () => {
            setIsApiFetching(false);
        })
    }

    const setDefaultRole = (roleGroupId: string) => {
        setIsApiFetching(true);
        api.setDefaultRole(roleGroupId, () => {
            getClinics();
            getMunicipalities();
        }, undefined, () => setIsApiFetching(false));
    }

    const getOrganisationInfo = (organisationNumber: string, organisationName: string) => {
        return `${formatOrganisationNumber(organisationNumber)} - ${organisationName}`;
    }

    const getClinicInfo = (clinicNumber?: string, clinicName?: string) => {
        if (clinicNumber && clinicName) {
            return `${clinicNumber} - ${clinicName}`;
        } else {
            return "-";
        }
    }

    const getTitle = () => {
        var hasClinics = clinics && clinics.length > 0;
        var hasMunicipalities = municipalities && municipalities.length > 0;

        if (hasClinics && hasMunicipalities) {
            return "Välj rollgrupp";
        }
        else if (!hasClinics && hasMunicipalities) {
            return "Välj kommun/stadsdel";
        }
        else if (hasClinics && !hasMunicipalities) {
            return "Välj vårdgivare/mottagning";
        }

        return "Välj vårdgivare/mottagning";
    }

    return (
        <SpinnerWrapper isLoading={isApiFetching}>
            <div className="card">
                <div className="card-header">
                    <h2>{getTitle()}</h2>
                </div>
                {
                    clinics.length > 0 &&
                    <>
                        <div className="card-body">
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Vårdgivare</th>
                                        <th>Mottagning</th>
                                        <th>Roller</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        clinics.length > 0 ?
                                            clinics.map(p =>
                                                <tr key={p.roleGroupId}>
                                                    <td>{getOrganisationInfo(p.organisationNumber, p.organisationName)}</td>
                                                    <td>{getClinicInfo(p.clinicNumber, p.clinicName)}</td>
                                                    <td>{roles(p.roles, p.roleGroupId)}</td>
                                                    <td>
                                                        <Form onSubmit={setDefaultRole}>
                                                            <HiddenInput name="roleGroupId" value={p.roleGroupId.toString()} />
                                                            <CustomSubmitButton
                                                                disabled={p.roleGroupId === adminRoleId || p.isDefault}
                                                                title={p.roleGroupId === adminRoleId ? "Kräver ändring i menuett" : undefined}
                                                            >
                                                                {p.isDefault ? "Förvald" : "Sätt som förvald"}
                                                            </CustomSubmitButton>
                                                        </Form>
                                                    </td>
                                                    <td>
                                                        <CustomButton
                                                            buttonType={parseInt(selectedRoleGroup ?? "") !== p.roleGroupId ? ButtonType.general : ButtonType.success}
                                                            buttonText={parseInt(selectedRoleGroup ?? "") === p.roleGroupId ? "Vald" : "Välj"}
                                                            disabled={parseInt(selectedRoleGroup ?? "") === p.roleGroupId ? true : false}
                                                            onClick={() => setSelectedClinic(p)}
                                                            data-target="#confirm-clinic-selection-modal"
                                                            data-toggle="modal" />
                                                    </td>
                                                </tr>)
                                            : <tr><td colSpan={5}>Det finns inga mottagningar</td></tr>
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </>
                }
                {
                    municipalities.length > 0 &&
                    <>
                        <div className="card-body">
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Kommun</th>
                                        <th>Stadsdel</th>
                                        <th>Boenden</th>
                                        <th>Roller</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        municipalities.length > 0 ?
                                            municipalities.map(p =>
                                                <tr key={p.roleGroupId}>
                                                    <td>{p.municipalityName}</td>
                                                    <td>{p.districtName}</td>
                                                    <td>{p.residenceGroupName}</td>
                                                    <td>{roles(p.roles, p.roleGroupId)}</td>
                                                    <td>
                                                        <Form onSubmit={setDefaultRole}>
                                                            <HiddenInput name="roleGroupId" value={p.roleGroupId.toString()} />
                                                            <CustomSubmitButton
                                                                disabled={p.roleGroupId === adminRoleId || p.isDefault}
                                                                title={p.roleGroupId === adminRoleId ? "Kräver ändring i menuett" : undefined}
                                                            >
                                                                {p.isDefault ? "Förvald" : "Sätt som förvald"}
                                                            </CustomSubmitButton>
                                                        </Form>
                                                    </td>
                                                    <td>
                                                        <CustomButton
                                                            buttonType={parseInt(selectedRoleGroup ?? "") !== p.roleGroupId ? ButtonType.general : ButtonType.success}
                                                            buttonText={parseInt(selectedRoleGroup ?? "") === p.roleGroupId ? "Vald" : "Välj"}
                                                            disabled={parseInt(selectedRoleGroup ?? "") === p.roleGroupId ? true : false}
                                                            onClick={() => setSelectedMunicipality(p)}
                                                            data-target="#confirm-municipality-selection-modal"
                                                            data-toggle="modal" />
                                                    </td>
                                                </tr>)
                                            : <tr><td colSpan={7}>Det finns inga kommuner</td></tr>
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </>
                }
                <NavigateBackFooter />
            </div>
            <ConfirmClinicSelectionModal selectedClinic={selectedClinic} />
            <ConfirmMunicipalitySelectionModal selectedMunicipality={selectedMunicipality} />
        </SpinnerWrapper>
    );
}